import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { company, loanPrograms, residentialPrograms } from "../mock";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "Loan Programs", to: "/loan-programs", dropdown: true },
  { label: "Personal Loans", to: "/personal-loans" },
  { label: "Tools & Guides", to: "/tools" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [progOpen, setProgOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setProgOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50">
      {/* Announcement bar */}
      <div className="bg-[#0b1f3a] text-white/90 overflow-hidden border-b border-white/10">
        <div className="whitespace-nowrap py-2 text-xs sm:text-sm flex">
          <div className="ltr-marquee flex shrink-0">
            {[0, 1].map((r) => (
              <span key={r} className="flex items-center gap-8 pr-8">
                <span>Commercial Real Estate Financing up to <b className="text-[#c6a15b]">$100 Million</b></span>
                <span className="text-[#c6a15b]">★</span>
                <span>Get funded in <b className="text-[#c6a15b]">72 hours</b> or less</span>
                <span className="text-[#c6a15b]">★</span>
                <span>No obligation — <b className="text-[#c6a15b]">FREE consultation</b></span>
                <span className="text-[#c6a15b]">★</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div
        className={`transition-all duration-300 ${
          scrolled ? "bg-white shadow-[0_4px_30px_rgba(11,31,58,0.08)]" : "bg-white/95 backdrop-blur"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[76px]">
            {/* Logo */}
            <Link to="/" className="flex items-center group" aria-label="LTR Capital Partners home">
              <img src="/logo-transparent.png" alt="LTR Capital Partners" className="h-10 sm:h-11 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]" />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((l) =>
                l.dropdown ? (
                  <div key={l.to} className="relative group">
                    <NavLink
                      to={l.to}
                      className={({ isActive }) =>
                        `px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-1 transition-colors ${
                          isActive ? "text-[#0b1f3a]" : "text-slate-600 hover:text-[#0b1f3a]"
                        }`
                      }
                    >
                      {l.label}
                      <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
                    </NavLink>
                    <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                      <div className="w-[620px] bg-white rounded-xl shadow-2xl ring-1 ring-slate-100 p-4 grid grid-cols-2 gap-4">
                        <div>
                          <div className="px-3 pb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#c6a15b]">Commercial</div>
                          {loanPrograms.map((p) => (
                            <Link
                              key={p.slug}
                              to={`/loan-programs/${p.slug}`}
                              className="block px-3 py-2 rounded-lg hover:bg-slate-50 transition group/item"
                            >
                              <div className="text-sm font-semibold text-[#0b1f3a] group-hover/item:text-[#b08e46]">{p.title}</div>
                            </Link>
                          ))}
                        </div>
                        <div>
                          <div className="px-3 pb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#c6a15b]">Residential</div>
                          {residentialPrograms.map((p) => (
                            <Link
                              key={p.slug}
                              to={`/loan-programs/${p.slug}`}
                              className="block px-3 py-2 rounded-lg hover:bg-slate-50 transition group/item"
                            >
                              <div className="text-sm font-semibold text-[#0b1f3a] group-hover/item:text-[#b08e46]">{p.title}</div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    className={({ isActive }) =>
                      `px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                        isActive ? "text-[#0b1f3a]" : "text-slate-600 hover:text-[#0b1f3a]"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                )
              )}
            </nav>

            {/* Right actions */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href={`tel:${company.phoneTel}`}
                className="flex items-center gap-2 text-sm font-semibold text-[#0b1f3a] hover:text-[#b08e46] transition"
              >
                <Phone className="h-4 w-4" />
                {company.phoneDisplay}
              </a>
              <Link
                to="/apply"
                className="inline-flex items-center rounded-md bg-[#c6a15b] px-5 py-2.5 text-sm font-bold text-[#0b1f3a] hover:bg-[#d8bc85] transition-colors shadow-sm"
              >
                Apply Now
              </Link>
            </div>

            {/* Mobile toggle */}
            <button
              className="lg:hidden p-2 text-[#0b1f3a]"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-100 bg-white">
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((l) => (
                <div key={l.to}>
                  {l.dropdown ? (
                    <>
                      <button
                        onClick={() => setProgOpen((v) => !v)}
                        className="w-full flex items-center justify-between px-3 py-3 rounded-lg text-[#0b1f3a] font-semibold"
                      >
                        {l.label}
                        <ChevronDown className={`h-4 w-4 transition-transform ${progOpen ? "rotate-180" : ""}`} />
                      </button>
                      {progOpen && (
                        <div className="pl-3 space-y-1 pb-2">
                          <div className="px-3 pt-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#c6a15b]">Commercial</div>
                          {loanPrograms.map((p) => (
                            <Link
                              key={p.slug}
                              to={`/loan-programs/${p.slug}`}
                              className="block px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50"
                            >
                              {p.title}
                            </Link>
                          ))}
                          <div className="px-3 pt-3 text-[11px] font-bold uppercase tracking-[0.15em] text-[#c6a15b]">Residential</div>
                          {residentialPrograms.map((p) => (
                            <Link
                              key={p.slug}
                              to={`/loan-programs/${p.slug}`}
                              className="block px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50"
                            >
                              {p.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <NavLink
                      to={l.to}
                      className="block px-3 py-3 rounded-lg text-[#0b1f3a] font-semibold hover:bg-slate-50"
                    >
                      {l.label}
                    </NavLink>
                  )}
                </div>
              ))}
              <div className="pt-3 flex flex-col gap-2">
                <a
                  href={`tel:${company.phoneTel}`}
                  className="flex items-center justify-center gap-2 rounded-md border border-slate-200 px-4 py-3 font-semibold text-[#0b1f3a]"
                >
                  <Phone className="h-4 w-4" /> {company.phoneDisplay}
                </a>
                <Link
                  to="/apply"
                  className="rounded-md bg-[#c6a15b] px-4 py-3 text-center font-bold text-[#0b1f3a]"
                >
                  Apply Now
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
